import { action, makeObservable, observable, override } from 'mobx';
import { z } from 'zod';
import { RequireAllProps } from '../../../store/utils/Dto';
import { errorHandler, onError } from '../../../utils/errors';
import { defaultOnEmpty } from '../../../utils/object';
import ExtendedFeatures, {
  ExtendedFeaturesDto,
} from '../edit/extended/store/ExtendedFeatures';
import { VersionDto } from '../edit/files/store/Version';
import Versions from '../edit/files/store/Versions';
import Sellable, {
  SavingAction,
  SellableConstructorDto,
  SellableDto,
} from './sellable/Sellable';

export default class Product extends Sellable<ProductDto> {
  static Schema = z.object({
    name: z.string().min(1),
    sku: z.string().min(1),
    price: z.coerce.number().min(0),
    currency: z.string().min(1),
    downloadLimit: z.coerce.number().int().min(1),
    inPulseStore: z.boolean(),
    generateKeysAutomatically: z.boolean(),
    useCdn: z.boolean(),
    versions: z.any().superRefine((versions, ctx) => {
      const message = validateVersions(versions);
      if (message) {
        ctx.addIssue({ code: 'custom', message });
      }
    }),
  });

  coverUrl = '';
  downloadLimit = '10';
  generateKeysAutomatically = false;
  inPulseStore = false;
  useCdn = false;

  readonly type = 'product';
  readonly versions: Versions;
  readonly extendedFeatures: ExtendedFeatures;

  constructor(
    { versions, extendedFeatures, ...dto }: ProductConstructorDto = {},
    onSave?: (product: Product) => Promise<void>,
    private readonly onUpload?: (file: File) => Promise<string>,
  ) {
    super(dto, onSave as (product: Sellable) => Promise<void>);
    makeObservable<this, 'onAssetChanged'>(this, {
      coverUrl: observable,
      downloadLimit: observable,
      generateKeysAutomatically: observable,
      useCdn: observable,
      handleImageChange: action.bound,
      save: override,
      setCoverUrl: action.bound,
      onAssetChanged: action.bound,
    });

    Object.assign(this, dto);

    this.versions = new Versions(
      versions,
      this.forceEditMode,
      this.onAssetChanged,
    );

    this.extendedFeatures = new ExtendedFeatures(
      extendedFeatures,
      this,
      this.onAssetChanged,
    );

    this.validator.validate();
  }

  handleImageChange(file: File) {
    this.onUpload?.(file)
      .then((url) => {
        this.setCoverUrl(url);
        return this.onAssetChanged();
      })
      .catch(errorHandler('Cannot upload cover image'));
  }

  setCoverUrl(url: string) {
    this.coverUrl = url;
  }

  toJSON(): RequireAllProps<ProductDto> {
    return {
      ...super.toJSON(),
      coverUrl: this.coverUrl || undefined,
      downloadLimit: Number(this.downloadLimit),
      extendedFeatures: defaultOnEmpty(this.extendedFeatures.toJSON()),
      generateKeysAutomatically: this.generateKeysAutomatically || undefined,
      inPulseStore: this.inPulseStore || undefined,
      useCdn: this.useCdn || undefined,
      versions: this.versions.toJSON(),
    };
  }

  async save() {
    const errors = this.validate();
    if (errors.length > 0) {
      onError(
        new Error(`Product form isn't filled correctly: ${errors.join('. ')}`),
      );
      return undefined;
    }

    this.versions.updateAllIdIfNecessary();
    const result = await super.save();
    this.versions.markAllAsSaved();
    return result;
  }

  private async onAssetChanged() {
    this.setIsChanged(true);
    this.validate();
  }
}

function validateVersions(versions: Versions): string | undefined {
  if (versions.items.length === 0) {
    return 'Product should contain at least one version';
  }

  if (!versions?.isValid) {
    return "Some versions in the file section aren't filled correctly";
  }

  return undefined;
}

export interface ProductDto extends SellableDto {
  coverUrl?: string;
  downloadLimit?: number | string;
  extendedFeatures?: ExtendedFeaturesDto;
  generateKeysAutomatically?: boolean;
  inPulseStore?: boolean;
  useCdn?: boolean;
  versions?: VersionDto[];
}

export interface ProductConstructorDto
  extends SellableConstructorDto, ProductDto {
  savingAction?: SavingAction;
}

export interface ProductStats {
  forWebhooks?: number;
  labels: { count: number; label: string }[];
  statuses: { count: number; status: string }[];
  total: number;
}
